/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { State } from '@core/store';
import * as DriversActions from '@drivers/store/drivers.actions';
import { getDrivers, getIsDriverActionPending } from '@drivers/store/drivers.selectors';
import { Store } from '@ngrx/store';
import { Driver } from '@shared/models';
import { Observable } from 'rxjs';

export enum DriversTab {
  ActiveDrivers = 0,
  InactiveDrivers = 1
}

@Component({
    selector: 'app-drivers',
    templateUrl: './drivers.component.html',
    styleUrls: ['./drivers.component.scss'],
    standalone: false
})
export class DriversComponent implements OnInit {
  readonly tableColumns = [
    'firstName',
    'lastName',
    'department',
    'abbreviation',
    'licenseClass',
    'actions'
  ];
  drivers: Driver[] = [];
  dataSource = new MatTableDataSource<Driver>([]);
  actionPending$: Observable<boolean>;
  tab = DriversTab.ActiveDrivers;

  @ViewChild(MatSort) set matSort(sort: MatSort | undefined) {
    this.dataSource.sort = sort ?? null;
  }

  constructor(private store: Store<State>) { }

  ngOnInit(): void {
    this.dataSource.sortData = (drivers, sort) => this.sortDrivers(drivers, sort);

    this.store.select(getDrivers).subscribe(drivers => {
      this.drivers = drivers;
      this.onChangeTab(this.tab);
    });
    this.actionPending$ = this.store.select(getIsDriverActionPending);
    this.store.dispatch(new DriversActions.LoadDrivers());
  }

  onChangeTab(tab: DriversTab): void {
    this.tab = tab;
    this.dataSource.data = this.drivers.filter(
      driver => driver.enabled === (DriversTab.ActiveDrivers === tab)
    );
  }

  editDriver(driverId?: string): void {
    this.store.dispatch(new DriversActions.EditDriver(driverId));
  }

  deleteDriver(driverId: string): void {
    this.store.dispatch(new DriversActions.DeleteDriver(driverId));
  }

  deactivateDriver(driver: Driver): void {
    this.store.dispatch(new DriversActions.DeactivateDriver(driver));
  }

  /**
   * Sortiert nur die Abteilungsspalte, und zwar nach der fachlichen Reihenfolge
   * (sortOrder) statt alphabetisch - aufsteigend ergibt damit dieselbe Reihenfolge wie
   * die Liste unter dem Kalender. Personen ohne Abteilung stehen in beiden Richtungen
   * am Ende, weil eine fehlende Zuordnung kein Wert ist, der mitsortiert werden soll.
   * Ohne aktive Sortierung bleibt die Reihenfolge der API (Nachname, Vorname).
   */
  private sortDrivers(drivers: Driver[], sort: Sort): Driver[] {
    if ('department' !== sort.active || '' === sort.direction) {
      return drivers;
    }

    const factor = 'asc' === sort.direction ? 1 : -1;
    const withDepartment = drivers.filter(driver => !!driver.department);
    const withoutDepartment = drivers.filter(driver => !driver.department);

    withDepartment.sort((left, right) => {
      const bySortOrder = left.department.sortOrder - right.department.sortOrder;

      if (0 !== bySortOrder) {
        return factor * bySortOrder;
      }

      return left.lastName.localeCompare(right.lastName)
        || left.firstName.localeCompare(right.firstName);
    });

    return [...withDepartment, ...withoutDepartment];
  }
}
